import React from 'react';
import FeatureTile from '../FeatureTile';
import { useTileData } from '../../../hooks/useTileData';
import { getInteractionCounts } from '../../../api';
import { fmtNumber } from '../../shared/format';
import { useActiveRole } from '../../../hooks/useActiveRole';

const extractCount = (data, wanted) => {
  const row = (data?.by_modality || []).find((r) => r.modality === wanted);
  return Number(row?.count ?? 0);
};

// Format seconds as "3m 24s" for durations under an hour.
const fmtDur = (secs) => {
  if (secs == null || !Number.isFinite(Number(secs))) return '—';
  const n = Math.max(0, Math.round(Number(secs)));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const Icon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

export default function VoiceTile({ clientId, rangeStart, rangeEnd }) {
  const { data, loading, error } = useTileData(
    async () => {
      const resp = await getInteractionCounts(clientId, rangeStart, rangeEnd);
      return resp?.data ?? null;
    },
    [clientId, rangeStart, rangeEnd],
    Boolean(clientId && rangeStart && rangeEnd),
  );

  const total = Number(data?.total ?? 0);
  const voiceCount = extractCount(data, 'voice');
  const share = total > 0 ? (voiceCount / total) * 100 : 0;

  const primary = data ? {
    value: fmtNumber(voiceCount),
    label: 'Calls',
    subtitle: total > 0 ? `${share.toFixed(0)}% of all interactions` : 'This period',
  } : null;
  // avg_duration_seconds is aggregated across ALL conversations, not per-modality.
  const secondary = data ? [
    { value: fmtDur(data.avg_duration_seconds), label: 'Avg duration' },
    { value: fmtNumber(data.has_booking ?? 0), label: 'With booking' },
  ] : [];

  // 2026-07-28 — CTA route matches NavSidebarGrouped's chat/voice routing:
  // admin lands on ProjectDetailPage (/client/:id), client on
  // ClientProjectDetailPage (/client-projects/:id). Both filter the
  // transcript list by ?modality=voice. Hard-coded /voice used to 404.
  const role = useActiveRole().role;
  const isClient = role === 3;
  const ctaTo = clientId
    ? `${isClient ? '/client-projects' : '/client'}/${clientId}?modality=voice`
    : (isClient ? '/client-dashboard' : '/dashboard');

  return (
    <FeatureTile
      title="AI Voice"
      icon={<Icon />}
      color="blue"
      primary={primary}
      secondary={secondary}
      ctaLabel="Open"
      ctaTo={ctaTo}
      loading={loading}
      error={error}
    />
  );
}
